import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateRejectedPaymentByStudent, getAllPaymentByUserId } from '../../../../redux/actions/payment';
import { toast } from 'react-hot-toast';
import { ImageUploadDropzone } from '../../../../components/imageUpload/ImageUploadDropzone';
import { formatDateTime } from '../../../../common/formatDateText';
import { PaymentHistoryTimeline } from '../../../../components/paymentHistory/PaymentHistoryTimeline';

export const TabPaymentEdit = ({ userId, isOpen, onRegisterFooterActions, onUploadSuccess, rejectedPayment }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isImageLoading, setIsImageLoading] = useState(false);
    
    // Extraer información del pago rechazado
    const {
        paymentFolio = '',
        paymentType = '',
        createdAt = '',
        updatedAt = '',
        amount = 0,
        history = []
    } = rejectedPayment || {};

    // Cargar la imagen existente cuando se monta el componente o cambia el pago rechazado
    useEffect(() => {
        setIsInitialLoading(true);
        if (rejectedPayment?.urlName && !selectedFile) {
            // Verificar que la imagen se pueda cargar
            const img = new Image();
            img.src = rejectedPayment.urlName;
            img.onload = () => {
                setPreviewUrl(rejectedPayment.urlName);
                setIsInitialLoading(false);
            };
            img.onerror = () => {
                toast.error('No se pudo cargar la imagen del comprobante rechazado.');
                setIsInitialLoading(false);
            };
        } else {
            setIsInitialLoading(false);
        }
    }, [rejectedPayment?.urlName, selectedFile]);

    // Resetear formulario cuando se cierra el drawer
    useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setPreviewUrl(null);
            setLoading(false);
            setIsImageLoading(false);
        }
    }, [isOpen]);

    const handleFileChange = (file) => {
        if (file) {
            setIsImageLoading(true);
            // Validar que la imagen se pueda cargar correctamente
            const img = new Image();
            const reader = new FileReader();
            
            reader.onloadend = () => {
                img.src = reader.result;
                img.onload = () => {
                    // La imagen se cargó correctamente
                    setSelectedFile(file);
                    // Limpiar el previewUrl cuando se selecciona un nuevo archivo
                    setPreviewUrl(null);
                    setIsImageLoading(false);
                };
                img.onerror = () => {
                    // Error al cargar la imagen
                    toast.error('Error al cargar la imagen. Por favor, intenta con otra imagen.');
                    setIsImageLoading(false);
                };
            };
            
            reader.onerror = () => {
                toast.error('Error al leer el archivo. Por favor, intenta nuevamente.');
                setIsImageLoading(false);
            };
            
            reader.readAsDataURL(file);
        } else {
            setSelectedFile(file);
            setPreviewUrl(null);
        }
    };

    const handleFileRemove = () => {
        setSelectedFile(null);
        // Limpiar el previewUrl completamente para mostrar la zona de arrastre
        // permitiendo al usuario seleccionar una nueva imagen
        setPreviewUrl(null);
    };

    const handleSubmitUpdatePayment = useCallback(() => {
        // Validaciones
        if (!selectedFile) {
            toast.error('Debe seleccionar un nuevo comprobante de pago para actualizar.');
            return;
        }

        // Obtener el ID correcto (puede ser 'id' o '_id')
        const paymentId = rejectedPayment?._id || rejectedPayment?.id;
        
        if (!paymentId) {
            toast.error('Error: No se encontró el ID del pago.');
            console.error('rejectedPayment:', rejectedPayment);
            return;
        }

        // El publicId puede no existir en algunos casos
        const publicId = rejectedPayment?.publicId || '';

        if (!user?._id) {
            toast.error('Error: No se encontró el ID del usuario.');
            return;
        }

        setLoading(true);

        const payload = {
            userId,
            paymentId: paymentId,
            publicId: publicId,
            file: selectedFile,
            updatedBy: user._id
        };

        dispatch(updateRejectedPaymentByStudent(payload))
            .then((result) => {
                if (result.status === 200) {
                    toast.success(result.message);
                    setSelectedFile(null);
                    setLoading(false);
                    dispatch(getAllPaymentByUserId(userId));
                    // Cambiar al tab de historial después de actualizar exitosamente
                    if (onUploadSuccess) {
                        setTimeout(() => onUploadSuccess(), 500);
                    }
                } else {
                    toast.error(result.message || 'Error al actualizar el comprobante de pago');
                    setLoading(false);
                }
            })
            .catch((error) => {
                console.error('Error al actualizar pago:', error);
                toast.error('Error al actualizar el comprobante de pago');
                setLoading(false);
            });
    }, [userId, selectedFile, rejectedPayment, user, dispatch, onUploadSuccess]);

    // Registrar acciones del footer cuando este tab está visible
    useEffect(() => {
        if (!onRegisterFooterActions) return;
        onRegisterFooterActions({
            loading: loading || isImageLoading,
            disabled: loading || isImageLoading || !selectedFile,
            onSubmit: handleSubmitUpdatePayment,
            buttonLabel: 'Actualizar Comprobante'
        });
        // No limpiar al desmontar para evitar re-renders innecesarios
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, selectedFile, isImageLoading]);

    return (
        <div className='h-full flex flex-col'>
            {/* Header fijo */}
            <div className="flex-shrink-0 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Actualizar comprobante de pago
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5 hidden lg:block">
                                    Tu pago fue rechazado. Por favor, actualiza tu comprobante de pago.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido con scroll */}
            <div className="flex-1 overflow-y-auto pr-2">
                {isInitialLoading ? (
                    /* Skeleton de carga inicial */
                    <>
                        {/* Skeleton para información del pago */}
                        <div className="flex-shrink-0 mb-6">
                            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                                        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
                                        <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Skeleton para el dropzone */}
                        <div className='flex w-full flex-col items-center border-0 md:border-[1px] border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm bg-white mb-6'>
                            <div className='w-full grid grid-cols-1 gap-4'>
                                <div className="mb-2">
                                    <div className="h-5 bg-gray-200 rounded w-40 mb-1 animate-pulse"></div>
                                    <div className="h-3 bg-gray-200 rounded w-64 animate-pulse"></div>
                                </div>
                                <div className="w-full h-64 bg-gray-200 rounded-lg animate-pulse"></div>
                                <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Información del Pago Rechazado */}
                        <div className="flex-shrink-0 mb-6">
                            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="text-lg font-bold text-gray-800">Información del Pago</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Folio</label>
                                        <p className="text-[.9rem] font-medium text-gray-900">{`#${paymentFolio || '-'}`}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Tipo de Pago</label>
                                        <p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentType || '-'}</p>
                                    </div>
                                </div>

                                {/* Historial de Movimientos - Acordeón */}
                                {history && history.length > 0 && (
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                                            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-base font-bold text-gray-800">Historial de Movimientos</span>
                                                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full">
                                                    {history.length}
                                                </span>
                                            </div>
                                            <svg
                                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                                                    isHistoryExpanded ? 'rotate-180' : ''
                                                }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Contenido del acordeón */}
                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                                isHistoryExpanded ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                                            }`}
                                        >
                                            <PaymentHistoryTimeline history={history} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sección de actualización del comprobante */}
                        <div className='flex w-full flex-col items-center border-0 md:border-[1px] border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm bg-white mb-6'>
                            <div className='w-full grid grid-cols-1 gap-4'>
                                <div className="mb-2">
                                    <h4 className="text-md font-bold text-gray-800 mb-1">Comprobante de Pago</h4>
                                    <p className="text-xs text-gray-500">
                                        {previewUrl && !selectedFile 
                                            ? 'Comprobante actual rechazado. Haz clic en la X para seleccionar uno nuevo'
                                            : 'Selecciona un nuevo comprobante que cumpla con los requisitos'
                                        }
                                    </p>
                                </div>
                                
                                {/* Indicador de carga de imagen */}
                                {isImageLoading && (
                                    <div className="w-full p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                                        <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm font-medium text-blue-800">Cargando imagen...</span>
                                    </div>
                                )}
                            
                                <ImageUploadDropzone
                                    file={selectedFile}
                                    previewUrl={previewUrl}
                                    onFileChange={handleFileChange}
                                    onFileRemove={handleFileRemove}
                                    disabled={loading || isImageLoading}
                                    existingImageLabel={previewUrl && !selectedFile ? "Comprobante actual (rechazado)" : null}
                                />

                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-800 text-center">
                                        <svg className="w-4 h-4 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        {previewUrl && !selectedFile 
                                            ? 'Este comprobante fue rechazado. Elimínalo para subir uno nuevo'
                                            : 'Por favor, selecciona un nuevo comprobante de pago que cumpla con los requisitos'
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
